import type { dash } from "@dropbox/api-v2-client/types/dropbox_types";
import type { TextInputInputRefObject } from "@dropbox/dig-components/text_fields";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { PAP_Interact_SourceInput } from "@mirage/analytics/events/types/interact_source_input";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { callApiV2 } from "@mirage/service-dbx-api";
import { buildSummarizable } from "@mirage/service-dbx-api/service/utils";
import { tagged } from "@mirage/service-logging";
import { useTypeaheadSearch } from "@mirage/service-typeahead-search/hooks/useTypeaheadSearch";
import type { typeahead } from "@mirage/service-typeahead-search/service/types";
import { ResultType } from "@mirage/service-typeahead-search/service/types";
import { getSourceUUID } from "@mirage/shared/compose/compose-session";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import { isUrl } from "@mirage/shared/util/tiny-utils";
import { getBaseUrl } from "@mirage/shared/util/urlRulesUtil";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const logger = tagged("useSourcesSearch");

const VISIBLE_TYPEAHEAD_RESULTS = 4;
const VISIBLE_FULL_SEARCH_RESULTS = 10;
const VISIBLE_UNUSABLE_SEARCH_RESULTS = 10;

interface SearchState {
  currentQuery: string;
  lastSearch?: {
    query: string;
    results: dash.query_result_unionSearchResult[];
  };
  isUrlSearch?: boolean;
}

const MIN_FULL_SEARCH_QUERY_LENGTH = 3;

async function getSearchResults(query: string) {
  if (query.length < MIN_FULL_SEARCH_QUERY_LENGTH) {
    return { query, results: [] };
  }
  const response = await callApiV2("dcsSearch", {
    query_text: query,
    type: { ".tag": "do_not_include_answer" },
  });
  const results = response.results || [];
  return {
    query,
    results: results
      .filter(
        (result) =>
          result.query_result &&
          result.query_result[".tag"] === "search_result",
      )
      .map(
        (result) => result.query_result,
      ) as dash.query_result_unionSearchResult[],
  };
}

export interface UseSourcesSearchProps {
  existingSources: ChatSource[];
  logComposeEvent: (
    event: PAPEvent,
    overrides?: {
      actionSurfaceComponent?: ActionSurfaceComponent;
    },
  ) => void;
  matchesFilter: (source: ChatSource, appTypes: Set<string>) => boolean;
}

export interface UseSourcesSearchResult {
  searchState: SearchState;
  isSearchOpen: boolean;
  containerWidth: number;
  existingSourcesUUIDs: Set<string>;
  typeaheadResults: Recommendation[];
  fullSearchResults: dash.query_result_unionSearchResult[];
  unusableResults: dash.query_result_unionSearchResult[];
  inputRef: React.RefObject<TextInputInputRefObject>;
  containerRef: React.RefObject<HTMLDivElement>;
  searchResultsRef: React.RefObject<HTMLDivElement>;
  handleChangeQuery: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputClick: () => void;
  handleClearSearch: () => void;
  handleClickOutside: (event: Event) => void;
  handleContainerClick: (e: React.MouseEvent) => void;
  setIsSearchOpen: (isOpen: boolean) => void;
  getFilteredSources: (appTypes: Set<string>) => ChatSource[];
}

export const useSourcesSearch = ({
  existingSources,
  logComposeEvent,
  matchesFilter,
}: UseSourcesSearchProps): UseSourcesSearchResult => {
  const inputRef = useRef<TextInputInputRefObject>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const [searchState, setSearchState] = useState<SearchState>({
    currentQuery: "",
  });
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const existingSourcesUUIDs = useMemo(
    () => new Set(existingSources.map((source) => getSourceUUID(source)!)),
    [existingSources],
  );

  const typeaheadSearchConfig: typeahead.PerInstanceConfig = useMemo(
    () => ({
      isRecentQueriesEnabled: false,
      isNullStateUsesRecommendations: true,
      isPeopleFilterRowEnabled: false,
      isSummarizableRecommendationsOnly: true,
    }),
    [],
  );

  const typeaheadSearch = useTypeaheadSearch({
    isInstanced: true,
    featureLine: "compose",
    instanceConfig: typeaheadSearchConfig,
    actionSurfaceComponent: "compose_chat_pane",
    dashActionSurface: "side_panel",
  });

  const debouncedUpdateSearchResults = useMemo(() => {
    const updateSearchResults = async (query: string) => {
      logger.log("searching for query of length", query.length);
      const results = await getSearchResults(query);
      setSearchState((prev: SearchState) => {
        if (prev.currentQuery === query) {
          return {
            ...prev,
            lastSearch: results,
          };
        }
        return prev;
      });
    };
    return debounce(updateSearchResults, 1000, {
      leading: true,
      trailing: true,
    });
  }, []);

  const debouncedLogEvent = useMemo(() => {
    return debounce(
      (query: string) => {
        logComposeEvent(
          PAP_Interact_SourceInput({
            queryString: query,
          }),
        );
      },
      1000,
      { trailing: true },
    );
  }, [logComposeEvent]);

  const handleChangeQuery = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      const isUrlSearch = isUrl(query);
      setSearchState((prev: SearchState) => {
        if (prev.currentQuery === query) {
          return prev;
        }
        return {
          ...prev,
          currentQuery: query,
          isUrlSearch,
        };
      });
      setIsSearchOpen(true);
      debouncedUpdateSearchResults(query);
      debouncedLogEvent(query);
      typeaheadSearch.setTypeaheadQuery(query, false);
      typeaheadSearch.setCanShowTypeahead(true);
    },
    [debouncedLogEvent, debouncedUpdateSearchResults, typeaheadSearch],
  );

  const handleInputClick = useCallback(() => {
    setIsSearchOpen(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
    if (searchState.currentQuery === "") {
      typeaheadSearch.setTypeaheadQuery("", false);
      typeaheadSearch.setCanShowTypeahead(true);
    }
  }, [typeaheadSearch, searchState.currentQuery]);

  const handleClearSearch = useCallback(() => {
    setSearchState({
      currentQuery: "",
      lastSearch: undefined,
    });
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.focus();
    }
    typeaheadSearch.setTypeaheadQuery("", true);
    typeaheadSearch.setCanShowTypeahead(true);
  }, [typeaheadSearch]);

  const handleClickOutside = useCallback(
    (event: Event) => {
      if (searchResultsRef.current?.contains(event.target as Node)) {
        inputRef.current?.focus();
        return;
      }
      setIsSearchOpen(false);
    },
    [setIsSearchOpen],
  );

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      const width = containerRef.current.offsetWidth;
      setContainerWidth(width);
    }
  }, []);

  useEffect(() => {
    const shouldShowTypeahead = searchState.currentQuery === "";

    if (existingSources.length === 0) {
      setIsSearchOpen(true);

      if (shouldShowTypeahead) {
        setSearchState((prev) => ({
          ...prev,
          lastSearch: undefined,
        }));
        typeaheadSearch.setTypeaheadQuery("", true);
        typeaheadSearch.setCanShowTypeahead(true);
      }

      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    } else if (shouldShowTypeahead) {
      typeaheadSearch.setTypeaheadQuery("", true);
      typeaheadSearch.setCanShowTypeahead(true);
    }
  }, [existingSources.length, searchState.currentQuery]);

  const typeaheadResults: Recommendation[] = useMemo(() => {
    const results: Recommendation[] = [];
    for (const typeaheadResult of typeaheadSearch.typeaheadResults) {
      if (typeaheadResult.type === ResultType.Recommendation) {
        results.push(typeaheadResult.result);
      }
    }
    logger.log("typeaheadSearch results length:", results.length);
    return results;
  }, [typeaheadSearch.typeaheadResults]);

  const fullSearchResults = useMemo(() => {
    const results = searchState.lastSearch?.results || [];
    const typeaheadUUIDs = new Set(typeaheadResults.map((r) => r.uuid));
    const query = searchState.currentQuery;

    // If query is a URL, only return results with exact base URL match
    if (isUrl(query)) {
      return results.filter(
        (r) => r.url && getBaseUrl(r.url) === getBaseUrl(query),
      );
    }

    return results
      .filter((r) => isSearchResultSupported(r))
      .filter((r) => r.uuid && !typeaheadUUIDs.has(r.uuid));
  }, [
    searchState.lastSearch?.results,
    typeaheadResults,
    searchState.currentQuery,
  ]);

  const unusableResults = useMemo(() => {
    const results = searchState.lastSearch?.results || [];
    const query = searchState.currentQuery;

    // If query is a URL, only return unusable results with exact base URL match
    if (isUrl(query)) {
      return results.filter(
        (r) =>
          !isSearchResultSupported(r) &&
          r.url &&
          getBaseUrl(r.url) === getBaseUrl(query),
      );
    }

    return results.filter((r) => !isSearchResultSupported(r));
  }, [searchState.lastSearch?.results, searchState.currentQuery]);

  useEffect(() => {
    if (searchState.currentQuery === "") {
      typeaheadSearch.setTypeaheadQuery("", true);
      typeaheadSearch.setCanShowTypeahead(true);
    }
  }, [searchState.currentQuery, typeaheadSearch]);

  const getFilteredSources = useCallback(
    (appTypes: Set<string>) => {
      const sources: ChatSource[] = [];
      const query = searchState.currentQuery;

      // Helper to check if a result matches filter criteria
      const matchesSearchCriteria = (
        result: dash.query_result_unionSearchResult,
      ) => {
        return matchesFilter(
          { type: "dash_search_result", searchResult: result },
          appTypes,
        );
      };

      // For URL searches, only use full search results
      if (isUrl(query)) {
        fullSearchResults
          .filter((result) => !existingSourcesUUIDs.has(result.uuid || ""))
          .filter(
            (result) =>
              result.uuid !== undefined && matchesSearchCriteria(result),
          )
          .forEach((result) => {
            sources.push({
              type: "dash_search_result",
              searchResult: result,
            });
          });

        return sources;
      }

      // For non-URL searches, include all result types
      typeaheadResults
        .filter((result) => !existingSourcesUUIDs.has(result.uuid))
        .filter((result) =>
          matchesFilter(
            { type: "recommendation", recommendation: result },
            appTypes,
          ),
        )
        .slice(0, VISIBLE_TYPEAHEAD_RESULTS)
        .forEach((result) => {
          sources.push({
            type: "recommendation",
            recommendation: result,
          });
        });

      fullSearchResults
        .filter((result) => !existingSourcesUUIDs.has(result.uuid || ""))
        .filter(
          (result) =>
            result.uuid !== undefined && matchesSearchCriteria(result),
        )
        .slice(0, VISIBLE_FULL_SEARCH_RESULTS)
        .forEach((result) => {
          sources.push({
            type: "dash_search_result",
            searchResult: result,
          });
        });

      unusableResults
        .filter(
          (result) =>
            result.uuid !== undefined && matchesSearchCriteria(result),
        )
        .slice(0, VISIBLE_UNUSABLE_SEARCH_RESULTS)
        .forEach((result) => {
          sources.push({
            type: "dash_search_result",
            searchResult: result,
          });
        });

      return sources;
    },
    [
      typeaheadResults,
      fullSearchResults,
      unusableResults,
      existingSourcesUUIDs,
      matchesFilter,
      searchState.currentQuery,
    ],
  );

  return {
    searchState,
    isSearchOpen,
    containerWidth,
    existingSourcesUUIDs,
    typeaheadResults,
    fullSearchResults,
    unusableResults,
    inputRef,
    containerRef,
    searchResultsRef,
    handleChangeQuery,
    handleInputClick,
    handleClearSearch,
    handleClickOutside,
    handleContainerClick,
    setIsSearchOpen,
    getFilteredSources,
  };
};

export function isSearchResultSupported(
  result: dash.query_result_unionSearchResult,
) {
  return (
    buildSummarizable(result.summarizable) === "yes_summarizable" ||
    result.answerable?.[".tag"] === "yes_answerable"
  );
}
